import { useI18n } from '@/libraries/contexts/i18n-context';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Tooltip from '@/components/molecules/tooltip/tooltip';
import { useFlightSelectorFormatTime } from '../../flight-selector-helpers/use-flight-selector-format-time';
import styles from './flight-selector-card-section.module.scss';

interface FlightSelectorFlightCardSectionProps
  extends Content.Properties.FlightDetails {
  /** The headline to show when there is no time */
  noTimeHeadline: string;
  /** The text to show when there is no time */
  noTimeText: string;
}

const FlightSelectorFlightCardSection = ({
  airlineCompanyLogoUrl,
  airlineCompanyName,
  arrivalCode,
  arrivalDate,
  arrivalName,
  charterType,
  departureCode,
  departureDate,
  departureName,
  isDirect,
  noTimeHeadline,
  noTimeText,
  showTravelTime,
  stops,
  travelDays = 0,
}: FlightSelectorFlightCardSectionProps) => {
  const { t } = useI18n();
  const {
    arrivalHour,
    dateMonth,
    departureHour,
    differenceHours,
    differenceMinutes,
  } = useFlightSelectorFormatTime({
    arrivalDate,
    departureDate,
  });

  const duration = `${t('items.common.words.hoursShort', { hours: differenceHours.toFixed(0) })} ${t('items.common.words.minutesShort', { minutes: differenceMinutes.toFixed(0).padStart(2, '0') })}`;
  const formattedDate = [departureHour, arrivalHour]
    .filter(Boolean)
    .join(' - ')
    .toLowerCase();

  const renderDuration = () => {
    if (!showTravelTime) {
      return (
        <Typography className={styles.duration} tagStyle="bodyMediumBold">
          <Tooltip text={noTimeText}>{noTimeHeadline}</Tooltip>
        </Typography>
      );
    }

    return (
      <Typography className={styles.duration} tagStyle="bodyMediumBold">
        {duration}
      </Typography>
    );
  };

  const flightTypeLabel =
    charterType === 'scheduled'
      ? t('items.modules.flightSelector.flight.scheduledFlight')
      : charterType === 'charter'
        ? t('items.modules.flightSelector.flight.charterFlight')
        : null;

  const routeLabel = isDirect
    ? t('items.modules.flightSelector.flight.direct')
    : t('items.modules.flightSelector.flight.stops', { stops });

  const flightType = flightTypeLabel
    ? isDirect
      ? `${routeLabel} ${flightTypeLabel}`
      : `${routeLabel} - ${flightTypeLabel}`
    : routeLabel;

  return (
    <div className={styles.component}>
      <Typography className={styles.flightType} tagStyle="labelSmall">
        {`${flightType} / ${dateMonth}`}
      </Typography>

      <div className={styles.flightDetails}>
        <div className={styles.flex}>
          {airlineCompanyLogoUrl && (
            <Image
              alt={airlineCompanyName}
              className={styles.logo}
              height={32}
              src={airlineCompanyLogoUrl}
              unoptimized
              width={32}
            />
          )}

          <div className={styles.infoWrapper}>
            <div className={styles.formattedDateWrapper}>
              <Typography tagStyle="bodyMediumBold">{formattedDate}</Typography>

              {travelDays > 0 && (
                <Typography
                  className={styles.differenceDays}
                  tagStyle="labelSmall"
                >
                  {`+${travelDays}`}
                </Typography>
              )}
            </div>

            <Typography tagStyle="bodySmall">{airlineCompanyName}</Typography>
          </div>
        </div>

        <div className={styles.infoWrapper}>
          {renderDuration()}

          <Typography tagStyle="bodySmall">
            {`${departureName} (${departureCode})`}

            <Icon className={styles.arrow} name="ArrowRight" size={8} />

            {`${arrivalName} (${arrivalCode})`}
          </Typography>
        </div>
      </div>
    </div>
  );
};

FlightSelectorFlightCardSection.displayName = 'FlightSelectorFlightCardSection';

export default FlightSelectorFlightCardSection;
